import { useEffect, useRef, useState } from "react";
import { toPng } from "html-to-image";
import api from "@/lib/api";
import { toast } from "sonner";
import { ShareNetwork, Link as LinkIcon, DownloadSimple, WhatsappLogo } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { InviteCard } from "@/components/InviteCard";

// Build the public share URL for an invite card.
export const inviteUrl = (type, id) => `${window.location.origin}/invite/${type === "institution" ? "inst" : "group"}/${id}`;

// Reusable share actions row (copy link, WhatsApp, download PNG).
export function InviteShareActions({ type, id, cardRef, name }) {
  const url = inviteUrl(type, id);
  const [downloading, setDownloading] = useState(false);
  const copy = async () => { try { await navigator.clipboard.writeText(url); toast.success("Invite link copied"); } catch { toast.error("Could not copy"); } };
  const whatsapp = () => {
    const text = `Check out ${name || "this community"} on AlumniPulse — explore, reconnect and join the discussion: ${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
  };
  const download = async () => {
    if (!cardRef?.current) return;
    setDownloading(true);
    try {
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 2, cacheBust: true });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `${(name || "alumniglobal").toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40)}-alumniglobal.png`;
      a.click();
      toast.success("Card image downloaded — share it anywhere!");
    } catch { toast.error("Could not generate the image"); }
    finally { setDownloading(false); }
  };
  return (
    <div className="flex flex-wrap gap-2">
      <Button variant="outline" className="rounded-full gap-1.5" onClick={copy} data-testid="invite-copy"><LinkIcon size={15} /> Copy link</Button>
      <Button variant="outline" className="rounded-full gap-1.5" onClick={whatsapp} data-testid="invite-whatsapp"><WhatsappLogo size={15} weight="fill" /> WhatsApp</Button>
      <Button className="rounded-full gap-1.5" onClick={download} disabled={downloading} data-testid="invite-download"><DownloadSimple size={15} /> {downloading ? "Preparing…" : "Download card"}</Button>
    </div>
  );
}

// Button + dialog that fetches an invite card and offers share actions.
export default function InviteDialog({ type, id, label = "Share invite", variant = "outline", size = "default", className = "" }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const cardRef = useRef(null);
  useEffect(() => {
    if (open && !data && id) {
      api.get(`/public/invite/${type}/${id}`).then((r) => setData(r.data)).catch(() => setData(false));
    }
  }, [open, data, type, id]);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant={variant} size={size} className={`gap-2 rounded-full ${className}`} data-testid="invite-open"><ShareNetwork size={16} weight="duotone" /> {label}</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-md" data-testid="invite-dialog">
        <DialogHeader>
          <DialogTitle>Send an invite card</DialogTitle>
          <DialogDescription>Share this branded card so others explore, join and contribute to the discussion.</DialogDescription>
        </DialogHeader>
        {data === false ? <p className="text-sm text-muted-foreground">Could not load the invite.</p>
          : !data ? <div className="h-64 animate-pulse rounded-3xl bg-muted/40" />
          : (
            <div className="space-y-4">
              <div className="flex justify-center"><InviteCard ref={cardRef} data={data} /></div>
              <InviteShareActions type={type} id={id} cardRef={cardRef} name={data.name} />
            </div>
          )}
      </DialogContent>
    </Dialog>
  );
}
